import { useMemo } from "react";
import { Card, CardContent, CircularProgress } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import {
    MdOutlineInbox,
    MdOutlineHourglassEmpty,
    MdOutlinePriorityHigh,
} from "react-icons/md";
import { useAttentionQueue } from "hooks/useTicketsAttentionQueue";
import { AttentionTicketItem } from "interfaces/entities/ticketStats";
import moment from "moment";

type BucketKey = "unassigned" | "waitingTooLong" | "highPriority";

type BucketDef = {
    key: BucketKey;
    title: string;
    Icon: React.ComponentType<{ size?: number | string; color?: string }>;
    accent: string;          // акцентный цвет для иконки/индикатора
    accentSoft: string;      // мягкий фон под иконку
    href: string;            // куда ведёт «открыть всё»
    emptyText: string;
    subtitle?: (count: number, threshold?: number) => string;
};

const customerName = (c: AttentionTicketItem["customer"]): string => {
    if (!c) return "Без клиента";
    const parts = [c.firstName, c.lastName].filter(Boolean);
    return parts.join(" ") || "Клиент";
};

const TicketRow = ({ ticket, suffix }: { ticket: AttentionTicketItem; suffix?: string }) => {
    return (
        <Link
            to={`/admin/tickets/${ticket.id}`}
            style={{
                display: "block",
                padding: "8px 10px",
                borderRadius: 6,
                textDecoration: "none",
                color: "inherit",
                transition: "background-color 120ms",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--hover-background-color, #F8FAFC)")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
        >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "baseline" }}>
                <div
                    style={{
                        fontSize: 13,
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        flex: 1,
                    }}
                    title={ticket.subject}
                >
                    #{ticket.id} · {ticket.subject || "Без темы"}
                </div>
                <div style={{ fontSize: 11, color: "#94A3B8", whiteSpace: "nowrap" }}>
                    {suffix || moment(ticket.createdAt).fromNow()}
                </div>
            </div>
            <div style={{ fontSize: 11, color: "#64748B", marginTop: 2 }}>
                {customerName(ticket.customer)}
                {ticket.branch?.name ? ` · ${ticket.branch.name}` : ""}
            </div>
        </Link>
    );
};

const SkeletonRow = () => (
    <div
        style={{
            height: 38,
            borderRadius: 6,
            background:
                "linear-gradient(90deg, #F1F5F9 0%, #E2E8F0 50%, #F1F5F9 100%)",
            backgroundSize: "200% 100%",
            animation: "shimmer 1.4s infinite",
        }}
    />
);

const BucketCard = ({
    bucket,
    count,
    items,
    isLoading,
    suffixForItem,
}: {
    bucket: BucketDef;
    count: number;
    items: AttentionTicketItem[];
    isLoading: boolean;
    suffixForItem?: (t: AttentionTicketItem) => string;
}) => {
    const navigate = useNavigate();

    return (
        <Card
            sx={{
                borderRadius: 2,
                boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 1px 3px rgba(15,23,42,0.04)",
                border: `1px solid ${bucket.accentSoft}`,
                transition: "transform 120ms, box-shadow 120ms",
                "&:hover": { boxShadow: "0 4px 10px rgba(15,23,42,0.06)" },
            }}
        >
            <CardContent>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: 12,
                    }}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div
                            style={{
                                width: 36,
                                height: 36,
                                borderRadius: 8,
                                background: bucket.accentSoft,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <bucket.Icon size={20} color={bucket.accent} />
                        </div>
                        <div>
                            <div style={{ fontSize: 12, color: "#64748B", lineHeight: 1.2 }}>
                                {bucket.title}
                            </div>
                            <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2, color: bucket.accent }}>
                                {isLoading ? "—" : count}
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={() => navigate(bucket.href)}
                        style={{
                            border: "none",
                            background: "transparent",
                            color: "#64748B",
                            fontSize: 12,
                            cursor: "pointer",
                            padding: "4px 8px",
                            borderRadius: 6,
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#F1F5F9")}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                    >
                        Все →
                    </button>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 4, minHeight: 120 }}>
                    {isLoading && (
                        <>
                            <SkeletonRow />
                            <SkeletonRow />
                            <SkeletonRow />
                        </>
                    )}

                    {!isLoading && items.length === 0 && (
                        <div
                            style={{
                                color: "#94A3B8",
                                fontSize: 12,
                                textAlign: "center",
                                padding: "24px 8px",
                            }}
                        >
                            {bucket.emptyText}
                        </div>
                    )}

                    {!isLoading &&
                        items.map((t) => (
                            <TicketRow
                                key={t.id}
                                ticket={t}
                                suffix={suffixForItem ? suffixForItem(t) : undefined}
                            />
                        ))}
                </div>
            </CardContent>
        </Card>
    );
};

export interface TicketsAttentionQueueProps {
    /** Порог «висит слишком долго» в часах. По умолчанию 24. */
    waitingHours?: number;
    /** Сколько тикетов показывать в каждой корзине. По умолчанию 5. */
    limit?: number;
}

/**
 * Виджет «Очередь требующая внимания» для дашборда. Три карточки рядом:
 *  - NEW без исполнителя (никто не взял)
 *  - WAITING_FOR_RESPONSE > N часов
 *  - HIGH priority в активных статусах
 */
export const TicketsAttentionQueue = ({ waitingHours = 24, limit = 5 }: TicketsAttentionQueueProps) => {
    const { data, isLoading } = useAttentionQueue({ waitingHours, limit });

    // «Все →» уводит на канбан, где статусы наглядны. Фильтры по статусу/приоритету
    // в /admin/tickets пока не поддерживаются на уровне query — drill-in делается
    // кликом по конкретной строке тикета (см. TicketRow).
    const buckets: BucketDef[] = useMemo(
        () => [
            {
                key: "unassigned",
                title: "Не назначены",
                Icon: MdOutlineInbox,
                accent: "#0EA5E9",
                accentSoft: "#E0F2FE",
                href: "/admin/tickets",
                emptyText: "Все новые тикеты разобраны",
            },
            {
                key: "waitingTooLong",
                title: `В ожидании > ${waitingHours}ч`,
                Icon: MdOutlineHourglassEmpty,
                accent: "#F59E0B",
                accentSoft: "#FEF3C7",
                href: "/admin/tickets",
                emptyText: "Никто не висит без ответа",
            },
            {
                key: "highPriority",
                title: "Высокий приоритет",
                Icon: MdOutlinePriorityHigh,
                accent: "#EF4444",
                accentSoft: "#FEE2E2",
                href: "/admin/tickets",
                emptyText: "Срочных тикетов нет",
            },
        ],
        [waitingHours],
    );

    if (!data && isLoading) {
        return (
            <div className="flex justify-center" style={{ padding: 32 }}>
                <CircularProgress size={24} />
            </div>
        );
    }

    const u = data?.buckets.unassigned;
    const w = data?.buckets.waitingTooLong;
    const h = data?.buckets.highPriority;

    return (
        <div>
            <style>{`
                @keyframes shimmer {
                    0% { background-position: -200% 0; }
                    100% { background-position: 200% 0; }
                }
            `}</style>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12 }}>
                <h3 style={{ margin: 0 }}>Очередь требующая внимания</h3>
                <span style={{ fontSize: 12, color: "#94A3B8" }}>обновляется автоматически</span>
            </div>
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                    gap: 16,
                }}
            >
                <BucketCard
                    bucket={buckets[0]}
                    count={u?.count ?? 0}
                    items={u?.items ?? []}
                    isLoading={isLoading}
                />
                <BucketCard
                    bucket={buckets[1]}
                    count={w?.count ?? 0}
                    items={w?.items ?? []}
                    isLoading={isLoading}
                    suffixForItem={(t) =>
                        `висит ${moment(t.updatedAt).fromNow(true)}`
                    }
                />
                <BucketCard
                    bucket={buckets[2]}
                    count={h?.count ?? 0}
                    items={h?.items ?? []}
                    isLoading={isLoading}
                />
            </div>
        </div>
    );
};

export default TicketsAttentionQueue;
